// The SSO client — infrastructure for the Meet website's central-SSO surfaces
// (create + salesperson-join). Talks to the central IdP at AUTH_URL with raw
// `fetch` (NOT the backend http client, which targets Meet's own API on a
// different origin). Cookie-bearing calls MUST send `credentials: 'include'`;
// the `refresh_token` cookie is HttpOnly, so only the short-lived access token
// ever lives in JS — held in token.ts's memory, never web storage.
//
// Verified contract (MIHIR-RESPONSE-web-token-mechanism.md §Q2):
//   POST /api/v1/auth/refresh → no body, cookie in → { accessToken, user }, rotates cookie
//   POST /api/v1/auth/logout  → no body, cookie in → clears session + cookie
//   Access token: RS256, ~900s lifetime.
//
// The IdP mounts its auth routes under a global `/api/v1` prefix — every product
// calls it as `/api/v1/auth/...`, not `/auth/...`. Missing this prefix 404s
// silently and the app never gets past the loading state.
//
// ── Why this file was hardened (was a 123-line stripped copy) ──────────────────
// The previous version had NO session-generation guard and NO `terminating`
// flag, so a silent refresh scheduled ~13min out (or one already in flight)
// could resolve just AFTER logout and write a fresh access token back into
// memory / rotate the cookie — re-minting the very session the user just ended
// ("sign-out doesn't work / auto signed back in"). This version mirrors the
// robust Sales/DNDD client: every async step is fenced by a captured
// `generation`, logout bumps the generation + sets `terminating` + cancels the
// timer, and the actual server revocation goes through the central `/logout`
// ceremony (which double-revokes to beat an in-flight refresh from the page we
// left). Cross-tab logout is propagated so a sign-out in one tab tears the
// others down too.

import { AUTH_URL, LOGIN_URL } from '@/config/env';
import { installAuthRecovery } from '@/services/http/client';
import {
  getAccessToken,
  isHostInjected,
  isOwnAccessToken,
  setAccessToken,
  setCurrentUser,
  type CurrentUser,
} from './token';

// Lets the http client recover from an expired token without importing this
// feature (it must not — see its file header). Registered at module load, so
// it is in place before any request can be made. `refresh` is already
// de-duped and cross-tab locked, which is exactly what a burst of parallel
// 401s needs.
installAuthRecovery(async () => {
  // NEVER inside the mobile app's WebView. There is no refresh cookie in that
  // cookie jar — the native app owns the session and pushes tokens in via
  // __NSI_MEET_SET_TOKEN__ — so /auth/refresh would 401, and this module's
  // failure path clears the access token on a failed refresh. That would take
  // a perfectly valid native-supplied token and null it, signing the rep out
  // mid-call to fix a problem they did not have.
  if (isHostInjected()) return false;
  return refresh();
}, getAccessToken, isOwnAccessToken);

// Token lives ~900s; refresh a couple of minutes early to avoid a 401 window.
const REFRESH_LEAD_MS = 13 * 60 * 1000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
// Coalesce concurrent refreshes (a burst of 401s) into one network call.
let inFlight: Promise<boolean> | null = null;
// Bumped by every local/remote logout; any async work tagged with an older
// generation must abandon its result rather than write stale auth state.
let sessionGeneration = 0;
let terminating = false;

const LOGOUT_CHANNEL = 'nsi-auth';
const LOGOUT_STORAGE_KEY = 'nsi-auth:logout';
let logoutChannel: BroadcastChannel | null = null;
let crossTabListenerInstalled = false;

/** Cancel any pending silent-refresh timer (logout / failed refresh). */
export function cancelScheduledRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleRefresh(generation: number): void {
  cancelScheduledRefresh();
  if (typeof window === 'undefined' || terminating || generation !== sessionGeneration) {
    return;
  }
  refreshTimer = setTimeout(() => {
    void refresh();
  }, REFRESH_LEAD_MS);
}

/** Narrow the untyped `/api/v1/auth/refresh` body to its `accessToken` string. */
function readAccessToken(body: unknown): string | null {
  if (typeof body === 'object' && body !== null && 'accessToken' in body) {
    const token = (body as { accessToken: unknown }).accessToken;
    return typeof token === 'string' && token.length > 0 ? token : null;
  }
  return null;
}

/** Narrow the same response's `user` object (fullName, phone — not on the
 *  JWT itself, see token.ts's getDisplayUser). */
function readUser(body: unknown): CurrentUser | null {
  if (typeof body !== 'object' || body === null || !('user' in body)) return null;
  const u = (body as { user: unknown }).user;
  if (typeof u !== 'object' || u === null) return null;
  const rec = u as Record<string, unknown>;
  return {
    fullName: typeof rec.fullName === 'string' ? rec.fullName : null,
    phone: typeof rec.phone === 'string' ? rec.phone : null,
  };
}

// ── Cross-tab refresh serialization (Web Locks) ────────────────────────────────
// The IdP ROTATES the refresh cookie on every /auth/refresh and treats a
// reused (already-rotated) token as a replay → it revokes the whole token
// family → 401 → the user is kicked out of every app at once. Two tabs (or a
// tab + a background timer) refreshing at the same time is exactly that race.
// The Web Locks API gives us a browser-wide mutex keyed by name, shared across
// every tab of this origin, so only ONE refresh rotates the cookie at a time;
// the rest queue and then rotate sequentially (safe). Auto-released if a tab
// crashes. Where Web Locks is unavailable we simply run directly (the in-tab
// `inFlight` coalescing still applies).
const REFRESH_LOCK = 'nsi-auth-refresh';

interface MinimalLockManager {
  request(name: string, callback: () => Promise<boolean>): Promise<boolean>;
}

function getLockManager(): MinimalLockManager | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const lm = (navigator as unknown as { locks?: MinimalLockManager }).locks;
  return lm && typeof lm.request === 'function' ? lm : undefined;
}

function withRefreshLock(fn: () => Promise<boolean>): Promise<boolean> {
  const lm = getLockManager();
  return lm ? lm.request(REFRESH_LOCK, fn) : fn();
}

async function performRefresh(generation: number): Promise<boolean> {
  if (terminating || generation !== sessionGeneration) return false;

  // Hold the cross-tab lock for the whole rotate-and-store step so no other tab
  // can present the same refresh token concurrently.
  return withRefreshLock(async () => {
    if (terminating || generation !== sessionGeneration) return false;
    try {
      const res = await fetch(`${AUTH_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (terminating || generation !== sessionGeneration) return false;
      if (!res.ok) {
        setAccessToken(null);
        setCurrentUser(null);
        return false;
      }
      const body: unknown = await res.json();
      const token = readAccessToken(body);
      if (terminating || generation !== sessionGeneration) return false;
      if (!token) {
        setAccessToken(null);
        setCurrentUser(null);
        return false;
      }
      setAccessToken(token);
      setCurrentUser(readUser(body));
      scheduleRefresh(generation);
      return true;
    } catch {
      if (terminating || generation !== sessionGeneration) return false;
      // Network/transport fault — treat as no session; caller decides next step.
      setAccessToken(null);
      setCurrentUser(null);
      return false;
    }
  });
}

/**
 * Mint a fresh access token from the refresh cookie. De-duped: concurrent
 * callers share one in-flight request. Returns true on success (token now
 * in memory).
 */
export function refresh(): Promise<boolean> {
  if (terminating) return Promise.resolve(false);
  if (inFlight) return inFlight;
  const generation = sessionGeneration;
  inFlight = performRefresh(generation);
  void inFlight.finally(() => {
    inFlight = null;
  });
  return inFlight;
}

// Page-load entry: try to mint a token from the cookie. Deliberately once-per-
// page-load — the resolved promise is cached and never reset, so React Strict
// Mode's remount, multiple providers, or repeated mounts can NEVER fire a second
// boot refresh that would rotate the cookie again and trip reuse detection. The
// live `refresh()` (scheduled) is separate and still re-runnable.
let bootPromise: Promise<boolean> | null = null;
export function bootstrap(): Promise<boolean> {
  if (!bootPromise) bootPromise = refresh();
  return bootPromise;
}

/** Send the user to the central login page with a return round-trip. */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const ret = encodeURIComponent(window.location.href);
  window.location.href = `${LOGIN_URL}?return=${ret}`;
}

function installCrossTabListener(): void {
  if (typeof window === 'undefined' || crossTabListenerInstalled) return;
  crossTabListenerInstalled = true;

  if ('BroadcastChannel' in window) {
    try {
      logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
      logoutChannel.addEventListener('message', () => {
        void terminateLocalSession(false);
      });
      return;
    } catch {
      logoutChannel = null;
    }
  }

  window.addEventListener('storage', (event) => {
    if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
      void terminateLocalSession(false);
    }
  });
}

function broadcastLogout(): void {
  installCrossTabListener();
  if (logoutChannel) {
    logoutChannel.postMessage({ type: 'logout', at: Date.now() });
    return;
  }
  try {
    window.localStorage.setItem(LOGOUT_STORAGE_KEY, String(Date.now()));
    window.localStorage.removeItem(LOGOUT_STORAGE_KEY);
  } catch {
    // Storage may be unavailable; local teardown still succeeds.
  }
}

/** Drop local auth immediately so nothing in flight can revive the session. */
function terminateLocalSession(notifyOtherTabs: boolean): void {
  if (!terminating) {
    terminating = true;
    sessionGeneration += 1;
    cancelScheduledRefresh();
    setAccessToken(null);
    setCurrentUser(null);
  }
  if (notifyOtherTabs && typeof window !== 'undefined') broadcastLogout();
}

/**
 * End the session everywhere. Tear down local auth first (so an in-flight or
 * scheduled refresh can never re-mint it), notify other tabs, then hand off to
 * the central `/logout` ceremony which revokes the shared cookie server-side
 * (with its own double-revoke to beat a refresh from the page we just left).
 */
export async function logout(): Promise<void> {
  if (terminating) return;
  terminateLocalSession(true);
  if (typeof window !== 'undefined') {
    window.location.replace(`${LOGIN_URL.replace(/\/+$/, '')}/logout`);
  }
}
